import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { logError, logInfo } from "./logger";
import { ScanUploader } from "@/services/ScanUploader";
import {
  SCAN_HEARTBEAT_KEY,
  SCAN_HEARTBEAT_STALE_MS,
} from "@/Utils/constants";

/**
 * Liveness of whichever page owns the scan in localStorage, from the heartbeat
 * ScanUploader stamps while a scan is in flight (see its startHeartbeat).
 *
 * This is the signal that actually answers the question the whole module is
 * asking. The 22 captured frames live only in the memory of the page that
 * captured them, so a scan is finalizable if and only if that page is still
 * running. Timers can only ever approximate that; the heartbeat measures it.
 *
 *  - "alive"   — a page stamped the clock moments ago. Never touch the scan;
 *                this covers another tab as well as this one.
 *  - "gone"    — a stamp exists but is stale. Positive proof the owning page
 *                died (tab evicted on minimise, reload, crash), so nothing can
 *                finalize the scan no matter how young the finalize claim is.
 *  - "unknown" — no stamp at all: a scan started before this build shipped, or
 *                a localStorage write that failed. Fall back to the
 *                conservative time-based graces below.
 */
type OwnerLiveness = "alive" | "gone" | "unknown";

const readOwnerLiveness = (): { state: OwnerLiveness; ageMs: number | null } => {
  const raw = localStorage.getItem(SCAN_HEARTBEAT_KEY);
  if (!raw) return { state: "unknown", ageMs: null };
  const ageMs = Date.now() - parseInt(raw, 10);
  // NaN (corrupt value) and negative ages (clock skew, or a stamp written by a
  // device whose clock is ahead) both fall through to "unknown" rather than
  // being treated as proof of anything.
  if (Number.isNaN(ageMs) || ageMs < 0) return { state: "unknown", ageMs: null };
  return { state: ageMs < SCAN_HEARTBEAT_STALE_MS ? "alive" : "gone", ageMs };
};

/**
 * How long a scanID may sit in localStorage with NO upload_processing doc
 * before we treat it as dead. Covers the window where /bin-predict hasn't
 * created the doc yet for an upload that is actually in flight (possibly in
 * another tab, which this tab cannot see). The server-side orphan sweep
 * reaps unclaimed docs after 10 minutes, so 15 minutes is comfortably past
 * every legitimate in-flight state.
 */
const DOC_MISSING_GRACE_SECONDS = 15 * 60;

/**
 * How long a finalize CLAIM (`submitting_timestamp`, written by
 * `claimAndCheckRecovery` when the user presses Submit) may sit unresolved
 * before we treat the scan as dead.
 *
 * This can be far shorter than the grace above because the two clocks measure
 * different things. `timestamp` (scan start) is HUMAN-paced — the user may
 * legitimately spend ten minutes filling in the form before submitting. A
 * claim, by contrast, is MACHINE-paced: everything between the claim and
 * `resolved` is /upload-photos + ScanModel.create, which the uploader itself
 * budgets at 1.5s (opt-out) / 6s (opt-in), plus ~15s for the recovery branch.
 * Three minutes is 30-120x that budget while still being well inside the
 * backend sweep's own 10-minute claim TTL.
 *
 * Why this matters: without it, a submission interrupted by a browser close
 * stays "Submitting scan ..." until the server sweep flags it — 10+ minutes of
 * a screen we KNOW is dead, because the 22 frames live only in the memory of
 * the uploader that is already gone.
 *
 * The residual risk is the multi-tab case: another tab holding the frames
 * could still be mid-finalize on a very slow network, and clearing the keys
 * under it makes `ScanModel.create` build `storagePath` from a null
 * localStorage read (see HERE_BE_DRAGONS § "storagePath is built from
 * localStorage"). Single-tab — the overwhelmingly common case — is fully
 * protected by the uploader-ownership check below. Lowering this further
 * should wait until `ScanModel.create` takes its `storagePath` from the `id`
 * parameter instead of localStorage.
 */
const FINALIZE_CLAIM_GRACE_SECONDS = 3 * 60;

/**
 * Upper bound on the whole reconcile. Screens await this BEFORE registering
 * their scans listener, so an unbounded `getDoc` (no offline cache, no
 * built-in timeout — see HERE_BE_DRAGONS § "one-shot getDoc") would hold the
 * avatars list on its spinner for as long as the network hangs. On timeout we
 * report `null` (fail open, exactly like a read error) and let the underlying
 * read settle on its own: if it eventually finds a dead scan it still clears
 * the keys — the clear re-checks its preconditions — and the next screen
 * mount reflects it.
 */
const RECONCILE_TIMEOUT_MS = 3000;

/**
 * Outcome of a reconcile pass:
 * - "dead":      the scan is unrecoverable (discarded / swept / never reached
 *                the server) — keys cleared; nothing was submitted.
 * - "submitted": the submission actually COMPLETED (upload_processing.resolved
 *                is true with no discard flags — only ScanModel.create's batch
 *                sets that combination) but ScanUploader.reset() never ran,
 *                e.g. the browser closed after the batch was sent but before
 *                the ack. Keys cleared; the scans/{id} doc exists.
 * - null:        nothing to do (no keys, this tab's uploader owns the scan,
 *                the doc is healthy/in-flight, or the check failed).
 */
export type ScanRecoveryOutcome = "dead" | "submitted" | null;

const clearScanKeys = () => {
  localStorage.removeItem("scanID");
  localStorage.removeItem("timestamp");
  // Drop the dead owner's stamp too, so it can't linger and be read against
  // some later scan.
  localStorage.removeItem(SCAN_HEARTBEAT_KEY);
};

/**
 * Reconcile the persisted scan state (localStorage scanID/timestamp) against
 * upload_processing/{scanID} — the doc the server-side pipeline and orphan
 * sweep actually maintain.
 *
 * Why: scanID/timestamp are only removed by ScanUploader.reset() (finalize
 * success or discard). If the browser is closed or reloaded mid-submission —
 * or any time between scan and Submit — the in-memory ScanUploader is gone
 * but the keys survive, and the UI trusts them forever: AvatarScreen and
 * AvatarSelect synthesize an eternal "Submitting Scans ..." card, SubmitScan /
 * AvatarGeneration (which mobile browsers restore directly on reopen) wait on
 * a fresh singleton that never emits, and a Submit press can never complete
 * (submitScan() on an empty uploader is a no-op). Meanwhile the server sweep
 * has long flagged the upload discarded_stale and deleted its GCS artifacts —
 * or, in the "submitted" case, the scan actually made it and only the local
 * cleanup was lost (whose leftovers later render a phantom duplicate
 * "Submitting Scans ..." card once the real scan leaves PROCESSING).
 *
 * Safety: we never clear while a page is provably alive on this scan (this
 * tab's uploader, or any tab's fresh heartbeat). Past that gate we clear when
 *   - discarded / discarded_stale            → "dead"
 *   - resolved without discard flags         → "submitted"
 *   - the owning page is gone (stale         → "dead"   ← the fast path
 *     heartbeat) and the scan is unfinished
 *   - no heartbeat at all, and either the    → "dead"   ← conservative
 *     finalize claim or the scan itself has            fallbacks
 *     aged out (3 min / 15 min)
 * We never clear while this tab's ScanUploader owns the scanID, and we
 * re-check both localStorage and the uploader right before removal so a new
 * scan started while the getDoc was in flight can't be wiped. A doc that is
 * healthy but unclaimed (e.g. reload right after upload) is left alone: the
 * sweep flags it within 10 minutes and the next reconcile clears it. On any
 * read failure (permission-denied during an auth blip, network) we keep the
 * state — failing open never loses a live scan.
 */
const runReconcile = async (
  source: string,
): Promise<ScanRecoveryOutcome> => {
  const scanID = localStorage.getItem("scanID");
  const timestampStr = localStorage.getItem("timestamp");
  if (!scanID) return null;

  // This tab's uploader is actively working on the scan — not orphaned.
  if (ScanUploader.getInstance().getScanId() === scanID) return null;

  // Some page stamped the heartbeat moments ago, so a live uploader owns this
  // scan even though it isn't ours (another tab). Leave it completely alone —
  // clearing the keys under a live upload would corrupt the storagePath that
  // ScanModel.create reads from localStorage.
  const owner = readOwnerLiveness();
  if (owner.state === "alive") return null;

  const nowSeconds = Math.floor(Date.now() / 1000);
  const ageSeconds = timestampStr
    ? nowSeconds - parseInt(timestampStr, 10)
    : null;

  let outcome: ScanRecoveryOutcome = null;
  let reason: string | null = null;
  let docFlags: Record<string, unknown> = {};

  try {
    const snapshot = await getDoc(doc(db, "upload_processing", scanID));
    if (snapshot.exists()) {
      const data = snapshot.data();
      // Order matters: the discard check MUST stay ahead of the resolved
      // check, or a swept scan gets reported as a successful submission.
      // `cleanupOrphanScans` writes only `discarded_stale`; `onDiscardUpdate`
      // then sets `resolved` on that same doc, so a swept scan ends up with
      // BOTH. Conversely `onDiscardUpdate` bails unless a discard flag is
      // already present ("No discard signal."), which is what makes
      // `resolved` with no discard flags reliable proof that the PWA's own
      // success batch wrote it — i.e. scans/{id} exists. (bodyscanner:
      // scripts/python/cleanupOrphanScans_cloudfunction/main.py,
      // scripts/python/onDiscardUpdate_cloudfunction/main.py)
      const claimAgeSeconds =
        typeof data.submitting_timestamp === "number"
          ? nowSeconds - data.submitting_timestamp
          : null;
      docFlags = {
        discarded: data.discarded === true,
        discarded_stale: data.discarded_stale === true,
        resolved: data.resolved === true,
        uploaded: data.uploaded === true,
        claimAgeSeconds,
      };
      if (data.discarded === true || data.discarded_stale === true) {
        outcome = "dead";
        reason = "upload_processing is discarded";
      } else if (data.resolved === true) {
        // resolved without discard flags is only ever written by
        // ScanModel.create's success batch: the submission completed but the
        // local reset() was lost (browser closed between send and ack).
        outcome = "submitted";
        reason =
          "upload_processing resolved — submission completed, local reset lost";
      } else if (owner.state === "gone") {
        // The owning page is provably dead and the scan never reached a
        // terminal state. It cannot progress from here — the 22 frames only
        // ever existed in that page's memory — so say so immediately instead
        // of leaving the user on "Submitting scan ...". This is the path that
        // covers the reported bug: minimise the browser mid-submit, the OS
        // evicts the tab, and the user is back within seconds.
        outcome = "dead";
        reason = "owning page is gone (stale heartbeat); scan cannot progress";
      } else if (
        claimAgeSeconds !== null &&
        claimAgeSeconds > FINALIZE_CLAIM_GRACE_SECONDS
      ) {
        // No heartbeat to go on (pre-heartbeat scan, or a failed write), so
        // fall back to the claim clock: Submit was pressed but finalize never
        // resolved. Slower than the heartbeat path, still far ahead of the
        // server sweep.
        outcome = "dead";
        reason = "finalize claimed but never resolved past grace";
      }
    } else if (owner.state === "gone") {
      outcome = "dead";
      reason = "owning page is gone (stale heartbeat); no upload_processing doc";
    } else if (ageSeconds === null || ageSeconds > DOC_MISSING_GRACE_SECONDS) {
      outcome = "dead";
      reason = "upload_processing doc missing past grace period";
    }
  } catch (error) {
    logError("[ScanRecovery] reconcile check failed — keeping scan state", {
      scanID,
      source,
      code: (error as { code?: string })?.code ?? null,
      message: (error as Error)?.message,
    });
    return null;
  }

  if (!outcome) return null;

  // Re-check right before clearing: a new scan may have started (new scanID
  // written) or the uploader may have picked this scan up while the getDoc
  // was in flight.
  if (
    localStorage.getItem("scanID") !== scanID ||
    ScanUploader.getInstance().getScanId() === scanID
  ) {
    return null;
  }

  clearScanKeys();
  logInfo("[ScanRecovery] cleared orphaned scan state", {
    scanID,
    source,
    outcome,
    reason,
    ageSeconds,
    ownerLiveness: owner.state,
    ownerHeartbeatAgeMs: owner.ageMs,
    ...docFlags,
  });
  return outcome;
};

/**
 * Bounded wrapper — see RECONCILE_TIMEOUT_MS. Callers can safely `await` this
 * on a render path: it always settles.
 */
export const reconcileOrphanedScanState = (
  source: string,
): Promise<ScanRecoveryOutcome> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<ScanRecoveryOutcome>((resolve) => {
    timer = setTimeout(() => {
      logInfo("[ScanRecovery] check timed out — proceeding without reconcile", {
        source,
        timeoutMs: RECONCILE_TIMEOUT_MS,
      });
      resolve(null);
    }, RECONCILE_TIMEOUT_MS);
  });

  // runReconcile never rejects (its own catch reports and returns null), so
  // the losing side of this race can't surface as an unhandled rejection.
  return Promise.race([runReconcile(source), timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

/** Floor/ceiling on how long the watch below waits between looks. */
const WATCH_MIN_DELAY_MS = 1_000;
const WATCH_MAX_DELAY_MS = 15_000;

/**
 * Keep watching a leftover scan until its fate is actually knowable, then
 * report it once.
 *
 * A single check on mount is not enough, because the heartbeat only *becomes*
 * decisive a few seconds after the owning page dies. Kill the browser and
 * reopen it 9 seconds later (the reported repro, confirmed in pwa_logs) and
 * the mount-time check sees a ~4s-old stamp, correctly answers "inconclusive"
 * — and with a one-shot call nothing ever looks again, so the user sits on
 * "Submitting scan ..." forever anyway. Screens that can display that state
 * must watch, not peek.
 *
 * Cost is deliberately low: while a stamp still looks fresh this only reads
 * localStorage and sleeps until that stamp would have aged out. It spends a
 * Firestore read only when the heartbeat has actually gone stale (or was never
 * there). A genuinely live neighbouring tab therefore costs nothing but a
 * timer — it keeps re-stamping, we keep waiting, and we notice within seconds
 * of it going away.
 *
 * Returns a cleanup function; call it from the effect that started the watch.
 */
export const watchOrphanedScanState = (
  source: string,
  onOutcome: (outcome: Exclude<ScanRecoveryOutcome, null>) => void,
): (() => void) => {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = (delayMs: number) => {
    if (stopped) return;
    const clamped = Math.min(
      Math.max(delayMs, WATCH_MIN_DELAY_MS),
      WATCH_MAX_DELAY_MS,
    );
    timer = setTimeout(() => void run(), clamped);
  };

  const run = async () => {
    if (stopped) return;

    const scanID = localStorage.getItem("scanID");
    // Nothing to watch, or this tab's own uploader is driving the scan.
    if (!scanID) return;
    if (ScanUploader.getInstance().getScanId() === scanID) return;

    const owner = readOwnerLiveness();
    if (owner.state === "alive") {
      // Someone stamped recently. Don't spend a Firestore read on a question
      // we already know the answer to — sleep until that stamp would be stale
      // and look again. A real owner will have re-stamped by then.
      schedule(SCAN_HEARTBEAT_STALE_MS - (owner.ageMs ?? 0) + 500);
      return;
    }

    const outcome = await reconcileOrphanedScanState(source);
    if (stopped) return;
    if (outcome) {
      onOutcome(outcome);
      return;
    }
    // Inconclusive for another reason (read failed, doc still healthy and
    // within its grace). Back off and try again.
    schedule(WATCH_MAX_DELAY_MS);
  };

  void run();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
};
